/**
 * Campo de carga adentro del ticket.
 *
 * A diferencia del overlay ⌘K, este no se abre ni se cierra: esta siempre ahi,
 * y despues de agregar un producto se limpia y SE QUEDA CON EL FOCO. Cargar
 * cuatro items es tipear, Enter, tipear, Enter — sin que se abra y se cierre un
 * modal cuatro veces.
 *
 * Comparte la logica de busqueda con el overlay (`useBusquedaProductos`), asi
 * que el mismo texto encuentra lo mismo en los dos lados.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Plus } from "lucide-react";

import type { Moneda } from "@tora/shared";
import { Importe } from "@/components/money/importe";
import { MuestraColor } from "@/components/catalogo/MuestraColor";
import { nombreCompleto } from "@/components/catalogo/nombre";
import { Aviso } from "@/components/ui/aviso";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { cn } from "@/lib/utils";
import { usadoSinPrecio } from "@/lib/ticket-store";
import {
  useBusquedaProductos,
  DISPONIBILIDAD,
  MINIMO_LETRAS,
  type ProductoEncontrado,
} from "@/lib/usar-busqueda";

/**
 * De a cuantos se piden los resultados.
 *
 * Ocho alcanza para un telefono: se busca "iphone 17 pro" y son cinco. No
 * alcanza para un accesorio. Hay ochenta y pico entre cargadores, cables y
 * adaptadores, que se diferencian por watts, ficha y marca, y "cargador" o
 * "cable tipo c" son legitimamente veinte. Ahi ocho no es una lista: es el
 * principio de una lista, y lo que falta es justo donde suele estar el que se
 * busca.
 *
 * Por eso se piden de a tandas en vez de pedir cincuenta siempre: el caso
 * comun sigue costando una consulta corta, y la lista larga la pide quien la
 * necesita. El tope es 50 porque ahi topa `GET /precios`.
 */
const TANDA = 8;
const TOPE = 50;

/** Mismo mapa que Inventario (MASTER §10): disponible success · a pedido info · sin stock destructive. */
const TONO_DISPONIBILIDAD: Record<
  ProductoEncontrado["disponibilidad"],
  NonNullable<BadgeProps["variant"]>
> = {
  DISPONIBLE: "success",
  A_PEDIDO: "info",
  SIN_STOCK: "destructive",
};

export interface BuscadorEnTicketProps {
  /** Moneda de facturacion del ticket: define en cual se muestra el precio. */
  moneda: Moneda;
  /** Si se muestran margenes. En el mostrador la pantalla la ve el cliente. */
  mostrarInternos?: boolean;
  onAgregar: (p: ProductoEncontrado) => void;
  autoFocus?: boolean;
  /**
   * Con el ticket vacio el campo respira un poco mas: es la unica accion
   * posible en ese momento. Una vez que hay items se achica para dejarle el
   * lugar a la lista. Es una diferencia de TAMAÑO, no de color.
   */
  destacado?: boolean;
  /**
   * Avisa cuando se empieza o se termina de buscar (hay texto en el campo). El
   * ticket lo usa en el celular para esconder lo de arriba mientras se busca:
   * con el teclado abierto se veía un resultado y medio.
   */
  onBuscandoChange?: (buscando: boolean) => void;
}

export function BuscadorEnTicket({
  moneda,
  mostrarInternos = true,
  onAgregar,
  autoFocus = false,
  destacado = false,
  onBuscandoChange,
}: BuscadorEnTicketProps): JSX.Element {
  const [texto, setTexto] = useState("");
  const buscando = texto.trim() !== "";
  useEffect(() => {
    onBuscandoChange?.(buscando);
  }, [buscando, onBuscandoChange]);
  const [indice, setIndice] = useState(0);
  const [limite, setLimite] = useState(TANDA);
  const inputRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);

  const { resultados, cargando, error, sinResultados } = useBusquedaProductos(texto, limite);

  useEffect(() => setIndice(0), [resultados]);

  /**
   * Todo cambio de texto pasa por aca.
   *
   * Cada busqueda nueva arranca con la tanda corta: si no, buscar un cargador
   * y despues un telefono seguiria pidiendo cincuenta para traer tres. Va en
   * la funcion y no en un efecto sobre `texto` porque el limite es
   * consecuencia de tipear, no un estado que haya que sincronizar despues.
   */
  const cambiarTexto = useCallback((t: string) => {
    setTexto(t);
    setLimite(TANDA);
  }, []);

  /**
   * Que la fila elegida se vea.
   *
   * La lista scrollea desde el sexto resultado, asi que bajar con la flecha ya
   * sacaba la seleccion de la vista antes de esto. Con la lista extendida a
   * cincuenta, el cursor se perdia del todo: se veia moverse el resaltado dos
   * filas y despues nada.
   */
  useEffect(() => {
    listaRef.current
      ?.querySelector(`[data-indice="${indice}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [indice]);

  const verMas = useCallback(() => {
    setLimite((n) => Math.min(n === TANDA ? TANDA * 3 : TOPE, TOPE));
    // El foco vuelve al campo: extender la lista es un paso al costado, no el
    // final de la carga. Quien apreto "Ver mas" sigue queriendo tipear.
    inputRef.current?.focus();
  }, []);

  const agregar = useCallback(
    (p: ProductoEncontrado) => {
      onAgregar(p);
      // Limpiar y retener el foco es lo que convierte esto en una carga
      // continua. Si el foco se perdiera, cada item costaria un clic extra.
      setTexto("");
      setIndice(0);
      setLimite(TANDA);
      inputRef.current?.focus();
    },
    [onAgregar],
  );

  const onTecla = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        cambiarTexto("");
        return;
      }
      if (resultados.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIndice((i) => (i + 1) % resultados.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIndice((i) => (i - 1 + resultados.length) % resultados.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const p = resultados[indice];
        if (p) agregar(p);
      }
    },
    [resultados, indice, agregar, cambiarTexto],
  );

  /**
   * Escribir en cualquier parte del ticket carga acá.
   *
   * El campo llega enfocado, pero el foco es fragil: un clic en la lista, en el
   * chip del cliente o en cualquier zona muerta lo suelta, y a partir de ahi
   * tipear no hacia nada. En un mostrador eso se traduce en tener que ir a
   * buscar el campo con el mouse en medio de una venta.
   *
   * Es el mismo criterio que en `PasoDestinatario`: la tecla no se pierde, se
   * agrega a lo que ya hubiera escrito.
   */
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1 || e.key === " ") return;

      // Ya escribiendo aca: lo maneja el navegador.
      if (document.activeElement === inputRef.current) return;

      const objetivo = e.target as HTMLElement | null;
      const etiqueta = objetivo?.tagName;
      // Otro campo tiene la palabra: el ⌘K, el selector de clientes, el precio
      // editado a mano de una linea.
      if (
        etiqueta === "INPUT" ||
        etiqueta === "TEXTAREA" ||
        etiqueta === "SELECT" ||
        objetivo?.isContentEditable
      ) {
        return;
      }
      if (document.querySelector('[role="dialog"]')) return;

      e.preventDefault();
      inputRef.current?.focus();
      setTexto((t) => t + e.key);
      setLimite(TANDA);
    };

    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, []);

  const abierto = texto.trim().length >= MINIMO_LETRAS;
  // La tanda volvio llena: probablemente hay mas del otro lado del limite.
  const hayMas = resultados.length >= limite && limite < TOPE;

  return (
    /*
      `p-3` parejo, sin el `pb-0` de antes. El anillo de foco se dibuja FUERA
      de la caja del campo, y sin padding abajo no tenia donde caer: aterrizaba
      justo encima del `border-b` de este mismo contenedor. Eso era la linea
      que chocaba.
    */
    <div className={cn("border-b border-border", destacado && "p-3")}>
      {/*
        `InputBusqueda` plano: la lupa, el spinner mientras busca y la X para
        limpiar son suyos; el manejo de teclado (flechas y Enter sobre el
        listbox) entra por `onKeyDown` como en cualquier input.

        Con el ticket vacio (`destacado`) el campo respira un poco mas, pero NO
        cambia de color. Antes era `bg-white` fijo, para que resaltara tambien
        en oscuro "donde el campo se perdia contra la tarjeta". El diagnostico
        era correcto y la cura equivocada: lo que faltaba era contraste de
        BORDE, no invertir el fondo. Y ya no hace falta que grite: el campo
        llega enfocado desde el paso previo, y cualquier tecla lo enfoca de
        nuevo.

        El anillo de foco va en la CAJA y no en el input (por eso el plano,
        sin ring propio): el control que la persona ve es la caja, y dos
        anillos dibujan un rectangulo adentro de otro.

        Y el anillo es UNO SOLO, el mismo que el de cualquier campo del
        sistema (`claseCampo`: `ring-2 ring-ring`, sin offset). Antes eran
        tres trazos apilados —borde de 1px en color de anillo, offset blanco
        de 1px y anillo de 2px— que con `--ring` casi negro en tema claro
        dibujaban una doble linea de 4px alrededor del campo. Ese era el
        "se rompe": no estaba roto el layout, estaba gritando el foco.
      */}
      <InputBusqueda
        ref={inputRef}
        apariencia="plano"
        valor={texto}
        onValorChange={cambiarTexto}
        onKeyDown={onTecla}
        autoFocus={autoFocus}
        cargando={cargando}
        etiqueta="Buscar producto para agregar a la venta"
        placeholder={destacado ? "Agregá el primer producto…" : "Agregá un producto…"}
        autoCorrect="off"
        spellCheck={false}
        aria-expanded={abierto}
        aria-autocomplete="list"
        className={cn(
          destacado
            ? "rounded-md border border-border bg-background focus-within:ring-2 focus-within:ring-ring [&_input]:h-12 [&_input]:text-base"
            : "m-1",
        )}
      />

      {abierto && (
        /*
          Dos cajas y no una: la de adentro scrollea, la de afuera no. El pie
          —las teclas y el "Ver mas"— vivia adentro del area con scroll, asi que
          con la lista larga quedaba al fondo de cincuenta filas. El boton para
          extender la lista no puede estar del otro lado del scroll que obliga a
          extender.
        */
        <div
          className={cn(
            destacado ? "mt-2 rounded-md border border-border" : "border-t border-border",
          )}
        >
          {/* Alto propio solo en la compu: en el celular la lista crece y se
              desliza la pantalla entera, sin una zona adentro de otra donde el
              dedo se trababa al llegar al final. */}
          <div ref={listaRef} className="md:max-h-72 md:overflow-y-auto" role="listbox">
            {error && (
              <Aviso tono="destructive" compacto className="m-2">
                {error}
              </Aviso>
            )}

            {sinResultados && (
              <div className="px-4 py-4 text-center text-sm text-muted-foreground">
                No encontré nada con «{texto.trim()}».
              </div>
            )}

            {resultados.map((p, i) => {
              const disp = DISPONIBILIDAD[p.disponibilidad];
              const precio = moneda === "ARS" ? Number(p.precio_ars) : Number(p.precio_usd);

              return (
                // <button> crudo: fila de listbox (role="option"), la maneja el
                // teclado del input y no la tabulacion.
                <button
                  key={p.producto_id}
                  type="button"
                  role="option"
                  data-indice={i}
                  aria-selected={indice === i}
                  onClick={() => agregar(p)}
                  // `onMouseMove` y no `onMouseEnter`: con el puntero quieto
                  // sobre la lista, cada busqueda nueva dibujaba filas debajo de
                  // el y el `mouseenter` movia la seleccion a esa fila despues
                  // del reset a la primera. Enter agregaba lo que estaba bajo el
                  // mouse, no lo primero que se veia. Solo un movimiento real
                  // del mouse elige.
                  onMouseMove={() => {
                    if (indice !== i) setIndice(i);
                  }}
                  className={cn(
                    "flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-2 text-left transition-colors duration-150",
                    indice === i ? "bg-accent" : "hover:bg-accent",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    {/* El nombre ARMADO: color, memoria y bateria salieron de
                      adentro del nombre, y sin ellos dos usados del mismo
                      modelo se leen igual en el momento de elegir cual se
                      vende (ver `catalogo/nombre.ts`). En el celular baja de
                      renglón en vez de cortarse: al lado del precio le
                      quedaban 138 px y «CARGADOR PD/Q…» no decía cuál era. */}
                    <div className="break-words text-sm font-medium text-foreground sm:truncate">
                      {nombreCompleto(p)}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                      <Badge variant={TONO_DISPONIBILIDAD[p.disponibilidad]}>{disp.texto}</Badge>
                      {p.color && <MuestraColor color={p.color} />}
                      {/* Dos usados del mismo modelo, color y bateria solo se
                          distinguen por el IMEI. */}
                      {p.imei ? (
                        <span className="tabular-nums text-muted-foreground">
                          IMEI …{p.imei.slice(-4)}
                        </span>
                      ) : null}
                      {p.disponibilidad === "DISPONIBLE" && (
                        <span className="text-muted-foreground">{Number(p.stock_actual)} u.</span>
                      )}
                      {/* Sin esto se ofrecia al costo, sin margen y sin aviso; al
                        elegirlo, el ticket lo frena (`usadoSinPrecio`). */}
                      {usadoSinPrecio(p) && <Badge variant="warning">Sin precio de venta</Badge>}
                      {mostrarInternos && sinCosto(p) && (
                        <span className="text-muted-foreground">· sin costo</span>
                      )}
                      {mostrarInternos && !sinCosto(p) && Number(p.margen_ars) > 0 && (
                        // `whitespace-nowrap`: en el celular el «·» quedaba solo en
                        // un renglón y cada resultado sumaba una línea vacía.
                        <span className="inline-flex items-center gap-1 whitespace-nowrap text-muted-foreground">
                          · margen <Importe monto={Number(p.margen_ars)} moneda="ARS" tamano="sm" />
                        </span>
                      )}
                    </div>
                  </div>

                  <Importe monto={precio} moneda={moneda} className="shrink-0" />

                  <Plus className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                </button>
              );
            })}
          </div>

          {resultados.length > 0 && (
            <div className="flex items-center gap-3 border-t border-border px-4 py-1 text-xs text-muted-foreground">
              <span className="hidden sm:inline">
                <Tecla>↑</Tecla> <Tecla>↓</Tecla> elegir
              </span>
              <span className="hidden sm:inline">
                <Tecla>Enter</Tecla> agregar
              </span>

              {/*
                Aparece solo cuando la tanda volvio llena, que es la unica
                señal de que puede haber mas: `buscar_precio` devuelve las
                filas que entran en el limite y no dice cuantas quedaron.
                Preguntar el total costaria una consulta por tecla para un
                numero que casi nunca se mira.
              */}
              {hayMas && (
                <Button
                  variant="ghost"
                  size="sm"
                  // El mousedown se frena para que el campo no pierda el foco
                  // ni por un cuadro: la carga sigue siendo tipear.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={verMas}
                  // Sin `cargando`: eso lo deshabilitaria mientras llega la
                  // tanda, y el spinner del campo ya cuenta esa historia. Un
                  // boton que se apaga justo cuando lo apretaste se siente
                  // roto, y el segundo clic se perderia.
                  className="ml-auto"
                >
                  Ver más
                  <ChevronDown aria-hidden="true" />
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Sin costo cargado el margen es el precio entero y no dice nada: se muestra
 * «sin costo» para que se vea que falta el dato. Si la API no manda el costo
 * (una version vieja), se sigue mostrando el margen como antes.
 */
function sinCosto(p: ProductoEncontrado): boolean {
  return p.costo_ars !== undefined && !(Number(p.costo_ars ?? 0) > 0);
}

function Tecla({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <kbd className="rounded-sm border border-border bg-muted px-1 py-0.5 text-xs">{children}</kbd>
  );
}
